// ipad-pwa/src/lib/crypto.js
// =============================================================================
// SubtleCrypto helpers for the Phase-2 sealed-token flow.
//
//   sealBundle(plaintextObj, halfA, halfB) →
//       { iv_b64, ciphertext_b64 }
//
//   unsealBundle(ciphertext_b64, iv_b64, halfA, halfB) →
//       parsed JSON object
//
//   hmacBodySignature(secretBytes, method, url, rawBodyBytes) →
//       hex string for the X-Body-Signature header
//
// The wrap key is `halfA XOR halfB` (32 bytes) imported as an AES-GCM key.
// All ciphertext + iv values shuttle between layers as base64 strings so they
// land cleanly in localStorage / network JSON.
// =============================================================================

const enc = new TextEncoder();
const dec = new TextDecoder();

export function b64ToBuf(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bufToB64(buf) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function bufToHex(buf) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) {
    s += bytes[i].toString(16).padStart(2, '0');
  }
  return s;
}

async function deriveWrapKey(halfA, halfB) {
  if (halfA.length !== 32 || halfB.length !== 32) {
    throw new Error('wrap-key halves must be 32 bytes each');
  }
  const raw = new Uint8Array(32);
  for (let i = 0; i < 32; i++) raw[i] = halfA[i] ^ halfB[i];
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** AES-GCM-encrypt a JSON-serialisable object using halfA XOR halfB. */
export async function sealBundle(plaintextObj, halfA, halfB) {
  const key = await deriveWrapKey(halfA, halfB);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const pt = enc.encode(JSON.stringify(plaintextObj));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, pt);
  return { iv_b64: bufToB64(iv), ciphertext_b64: bufToB64(new Uint8Array(ct)) };
}

/** Inverse of sealBundle. Throws on auth-tag mismatch or wrong key. */
export async function unsealBundle(ciphertext_b64, iv_b64, halfA, halfB) {
  const key = await deriveWrapKey(halfA, halfB);
  const iv = b64ToBuf(iv_b64);
  const ct = b64ToBuf(ciphertext_b64);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return JSON.parse(dec.decode(pt));
}

/**
 * Compute HMAC-SHA256(secret, "METHOD:URL:" || rawBody).
 * Returns hex string. Server verifies with the same construction
 * (see server/src/middleware/verifyBodySignature.js).
 */
export async function hmacBodySignature(secretBytes, method, url, rawBodyBytes) {
  const key = await crypto.subtle.importKey(
    'raw',
    secretBytes,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const prefix = enc.encode(`${method}:${url}:`);
  const buf = new Uint8Array(prefix.length + rawBodyBytes.length);
  buf.set(prefix, 0);
  buf.set(rawBodyBytes, prefix.length);
  const sig = await crypto.subtle.sign('HMAC', key, buf);
  return bufToHex(new Uint8Array(sig));
}
